import clsx from 'clsx';
import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Spinner } from '~/ui/core/spinner/Spinner';

import { WaveformTimeline } from './WaveformTimeline';

type RecordingOverlayProps = {
    readonly getAmplitude: () => number;
    readonly isFadingOut?: boolean;
    readonly isTranscribing: boolean;
    readonly recordingSeconds: number;
    readonly onStopRecording: () => void;
};

const formatTime = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;

export const RecordingOverlay: FunctionComponent<RecordingOverlayProps> = ({
    getAmplitude,
    isFadingOut = false,
    isTranscribing,
    recordingSeconds,
    onStopRecording,
}) => {
    const translate = useTranslate();

    return (
        <div
            className={clsx(
                'absolute inset-0 z-10 flex items-center gap-4xl pr-xl pl-3xl transition-opacity duration-[350ms] ease',
                isFadingOut ? 'pointer-events-none opacity-0' : 'opacity-100',
            )}
        >
            <WaveformTimeline getAmplitude={getAmplitude} paused={isTranscribing || isFadingOut} />

            <span className="shrink-0 text-body-s font-medium tabular-nums text-error-600 tracking-tight">
                {formatTime(recordingSeconds)}
            </span>

            {isTranscribing ? (
                <div className="flex size-4xl shrink-0 items-center justify-center">
                    <Spinner className="!mr-0 text-error-600" />
                </div>
            ) : (
                <button
                    aria-label={translate('common.stop-recording')}
                    className="flex size-4xl shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-error-100 p-0 transition-opacity duration-200 hover:opacity-80"
                    type="button"
                    onClick={onStopRecording}
                >
                    <div className="size-m rounded-sm bg-error-600" />
                </button>
            )}
        </div>
    );
};
